import type { CardUrlType } from '@blinq-types';
import Badge from '@components/Badge/Badge';
import { LockableTextField } from '@components/Card2/LockableTextField';
import { OptionalToggle } from '@components/Card2/OptionalToggle';
import { SharedIndividualSelector } from '@components/Card2/SharedIndividualSelector';
import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { getSuggestionsByFieldType, getURLSuggestionsByFieldType } from '@modules/card-helpers';
import { Box, Stack } from '@ui-lib';
import { useContext, useMemo } from 'react';

import SuggestionsSection, {
  useSuggestionsSection,
} from '../CardFormFields/SuggestionsSection/SuggestionsSection';
import { CardValueProviderSelector } from '../CardValueProviderSelector';
import styles from './CardItemEditor.module.scss';
import type { Stage, ValueProvider } from '.cache/__types__';

type CardItemEditorProps = {
  valueProvider: ValueProvider | undefined;
  tempValueProviderForTour?: ValueProvider | null;
  setValueProvider: (value: ValueProvider) => void;
  isOptional?: boolean;
  setIsOptional?: (value: boolean) => void;
  instruction?: string;
  setInstruction?: (value: string) => void;
  showLabelWhenIndividual?: boolean;
  label?: string;
  setLabel?: (value: string) => void;
  labelLocked?: boolean;
  setLabelLocked?: (value: boolean) => void;
  labelLockEnabled?: boolean;
  labelLockedByTemplate?: boolean;
  /* If the property/field is from a template */
  isTemplated?: boolean;
  stage?: Stage;

  children: React.ReactElement;
  type?: string;
  fieldType?: 'url' | 'phone' | 'email' | 'address';
};

export const CardItemEditor: React.FC<CardItemEditorProps> = ({
  valueProvider,
  tempValueProviderForTour,
  setValueProvider,
  isOptional,
  setIsOptional,
  label,
  setLabel,
  labelLocked,
  setLabelLocked,
  showLabelWhenIndividual,
  labelLockEnabled,
  labelLockedByTemplate,
  isTemplated,
  stage,
  children,
  type,
  fieldType,
}) => {
  const { isCardOwner, flavour, cardProfile } = useContext(cardEditContext);

  const isTemplate = flavour === CardFlavours.TEMPLATE;
  const isTeamCard = flavour === CardFlavours.TEAM;

  const isLinkedToProfile = valueProvider === 'profile';

  const suggestions = useMemo(() => {
    if (fieldType === 'url') {
      return getURLSuggestionsByFieldType(type as CardUrlType);
    }
    if (fieldType === 'phone' || fieldType === 'email' || fieldType === 'address') {
      return getSuggestionsByFieldType(fieldType)();
    }
  }, [fieldType, type]);

  const [selectedOption, setSelectedOption] = useSuggestionsSection(suggestions ?? [], label);

  const showEditValueUi = useMemo(() => {
    if (isTemplate) {
      return valueProvider === 'template';
    }

    // If a property/field is from a template, then all you can do is edit the value
    if (isTemplated) {
      return true;
    }

    if (isCardOwner) {
      return true;
    }

    if (stage === 'done') {
      return true;
    }

    if (flavour === CardFlavours.TEAM && !isCardOwner && valueProvider === 'member') {
      return false;
    }

    return true;
  }, [flavour, isCardOwner, isTemplate, isTemplated, stage, valueProvider]);

  const showTeamCardValueProviderSelector = useMemo(() => {
    if (isTemplate) {
      return false;
    }

    if (isCardOwner) {
      return false;
    }

    if (isTemplated) {
      return false;
    }

    if (stage === 'done') {
      return false;
    }

    if (
      flavour === CardFlavours.ADD_DETAILS ||
      flavour === CardFlavours.CLAIM ||
      flavour === CardFlavours.INVITE_TOKEN_CLAIM ||
      flavour === CardFlavours.PERSONAL
    ) {
      return false;
    }

    return true;
  }, [flavour, isCardOwner, isTemplate, isTemplated, stage]);

  const showOptionalToggle = useMemo(() => {
    if (isTemplate) {
      return valueProvider !== 'template';
    }

    if (isTemplated) {
      return false;
    }

    if (isTeamCard && !isCardOwner && stage !== 'done') {
      return valueProvider === 'member';
    }

    return false;
  }, [isCardOwner, isTeamCard, isTemplate, isTemplated, stage, valueProvider]);

  const showLabelInput = useMemo(() => {
    if (!showLabelWhenIndividual) {
      return false;
    }

    if (isTemplate) {
      return valueProvider !== 'template';
    }

    if (isTemplated) {
      return false;
    }

    if (stage === 'done') {
      return false;
    }

    if (isTeamCard && !isCardOwner) {
      return valueProvider === 'member';
    }

    return false;
  }, [isCardOwner, isTeamCard, isTemplate, isTemplated, showLabelWhenIndividual, stage, valueProvider]);

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);

    if (setLabel) {
      setLabel(newSelectedOption);
    }
  }

  const normalisedTemplateValueProvider = tempValueProviderForTour ?? valueProvider ?? undefined;

  return (
    <Box
      gap="small"
      display="flex"
      flexDirection="column"
      className={styles['card-property-edit-container']}
      paddingBottom="small"
    >
      {isLinkedToProfile && !isTemplate && (
        <Box
          className={styles['card-field-linked-badge']}
          title="This field is linked to a profile"
          aria-label="This field is linked to a profile"
        >
          <Badge size="regular" shape="rectangle">
            LINKED
          </Badge>
        </Box>
      )}

      {isTemplate && (
        <SharedIndividualSelector selected={normalisedTemplateValueProvider} onChange={setValueProvider} />
      )}

      {showTeamCardValueProviderSelector && (
        <CardValueProviderSelector
          selected={normalisedTemplateValueProvider}
          onChange={setValueProvider}
          memberOptionTitle={cardProfile?.given_name ?? undefined}
        />
      )}

      {showLabelInput && setLabel && setLabelLocked && (
        <Stack gap="small">
          <LockableTextField
            value={label ?? ''}
            onChange={(event) => setLabel(event.currentTarget.value)}
            locked={labelLocked ?? false}
            toggleLocked={labelLockEnabled ? () => setLabelLocked(!labelLocked) : undefined}
            label="Label (optional)"
            explanation={labelLockedByTemplate ? 'Locked by template' : undefined}
          />
          {suggestions && suggestions?.length > 0 && (
            <SuggestionsSection
              options={suggestions}
              onSelect={handleSelectSuggestion}
              selectedOption={selectedOption}
              subject="label"
            />
          )}
        </Stack>
      )}

      {showEditValueUi && children}

      <Stack gap="medium">
        {showOptionalToggle && setIsOptional && (
          <OptionalToggle isOptional={isOptional ?? false} onChange={setIsOptional} />
        )}
      </Stack>
    </Box>
  );
};
